import { Button } from '@shared/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { cn } from '@shared/utils/utils';
import { ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ProfitabilityDefaultShippingCost,
  ProfitabilityMissingDefaultShippingCost,
} from '../../models/Profitability';
import { formatMissingDefaultShippingServiceLabel } from '../../utils/defaultShippingCostsDisplay';
import { DefaultShippingCostsPanel } from './DefaultShippingCostsPanel';

export type DefaultShippingCostsListView = 'missing' | 'configured';

interface DefaultShippingCostsListProps {
  view: DefaultShippingCostsListView;
  onViewChange: (view: DefaultShippingCostsListView) => void;
  missingTotal?: number;
  missingRows: ProfitabilityMissingDefaultShippingCost[];
  isLoading: boolean;
  isError?: boolean;
  locale: string;
  pagination?: {
    currentPage: number;
    lastPage: number;
    total: number;
    onPrev: () => void;
    onNext: () => void;
    isFetching: boolean;
  };
  onConfigureMissing: (row: ProfitabilityMissingDefaultShippingCost) => void;
  onEditConfigured: (row: ProfitabilityDefaultShippingCost, serviceLabel: string) => void;
  onAddConfigured: () => void;
}

function FilterTab({
  active,
  onClick,
  children,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

export const DefaultShippingCostsList = ({
  view,
  onViewChange,
  missingTotal,
  missingRows,
  isLoading,
  isError,
  locale,
  pagination,
  onConfigureMissing,
  onEditConfigured,
  onAddConfigured,
}: DefaultShippingCostsListProps) => {
  const { t } = useTranslation('profitabilityModule');
  const showPagination = view === 'missing' && pagination && (pagination.total > 0 || pagination.lastPage > 1);
  const colCount = 6;

  const emptyMessage =
    view === 'missing' ? t('default_shipping_costs.empty_missing') : t('default_shipping_costs.empty');

  return (
    <>
      <div className="mb-4">
        <div className="inline-flex rounded-lg border border-border bg-muted/30 p-1">
          <FilterTab
            active={view === 'missing'}
            onClick={() => onViewChange('missing')}
            testId="profitability-shipping-costs-view-missing"
          >
            {t('default_shipping_costs.view_missing')}
            {missingTotal != null && missingTotal > 0 ? ` (${missingTotal})` : ''}
          </FilterTab>
          <FilterTab
            active={view === 'configured'}
            onClick={() => onViewChange('configured')}
            testId="profitability-shipping-costs-view-configured"
          >
            {t('default_shipping_costs.view_configured')}
          </FilterTab>
        </div>
      </div>

      {view === 'missing' ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">{t('default_shipping_costs.col_service')}</TableHead>
                  <TableHead className="text-right text-muted-foreground">
                    {t('default_shipping_costs.col_orders_count')}
                  </TableHead>
                  <TableHead className="text-right text-muted-foreground">
                    {t('default_shipping_costs.col_cost')}
                  </TableHead>
                  <TableHead className="text-right text-muted-foreground">
                    {t('default_shipping_costs.col_cod_fee_fixed')}
                  </TableHead>
                  <TableHead className="text-right text-muted-foreground">
                    {t('default_shipping_costs.col_cod_fee_percent')}
                  </TableHead>
                  <TableHead className="w-[120px] text-muted-foreground">
                    {t('default_shipping_costs.col_actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={colCount} className="py-10 text-center text-muted-foreground">
                      …
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && isError && (
                  <TableRow>
                    <TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">
                      {t('default_shipping_costs.list_error')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !isError && missingRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  !isError &&
                  missingRows.map((row) => (
                    <TableRow
                      key={`${row.team_id}-${row.courier_service_id}`}
                      className="border-border"
                      data-testid={`profitability-shipping-cost-missing-${row.team_id}-${row.courier_service_id}`}
                    >
                      <TableCell className="max-w-[320px] truncate text-sm text-foreground">
                        {formatMissingDefaultShippingServiceLabel(row)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {new Intl.NumberFormat(locale).format(row.orders_count ?? 0)}
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">—</TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">—</TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">—</TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5 border-border"
                          onClick={() => onConfigureMissing(row)}
                          data-testid={`profitability-shipping-cost-configure-${row.team_id}-${row.courier_service_id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          {t('default_shipping_costs.configure')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {showPagination && pagination && (
            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {t('default_shipping_costs.pagination', {
                  page: pagination.currentPage,
                  total: pagination.total,
                })}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-border"
                  disabled={pagination.currentPage <= 1 || pagination.isFetching}
                  onClick={pagination.onPrev}
                  data-testid="profitability-shipping-costs-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-border"
                  disabled={pagination.currentPage >= pagination.lastPage || pagination.isFetching}
                  onClick={pagination.onNext}
                  data-testid="profitability-shipping-costs-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <DefaultShippingCostsPanel locale={locale} onEdit={onEditConfigured} onAdd={onAddConfigured} />
      )}
    </>
  );
};
